import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useEffect, useState } from 'react';

type MonthRow = { month: number; label: string; value: number; orders: number; qty: number };
type YearRow = { year: number; months: MonthRow[] };
type CategoryRow = { category: string; value: number; qty: number; orders: number };
type WorkerRow = {
  worker: string;
  qty: number;
  orders: number;
  value: number;
  norm_hours: number;
  sewn_hours: number;
  performance: number;
  errors: number;
};
type DailyRow = { date: string; value: number; qty: number };
type WorkerMonths = { worker: string; months: { month: string; value: number }[] };

type StatsData = {
  years: YearRow[];
  year: number;
  daily: DailyRow[];
  /** Srovnání od 1. 1. do dneška (`through` = 'MM-DD') — ne celé měsíce. */
  ytd: {
    through: string;
    this_year: { value: number; qty: number };
    last_year: { value: number; qty: number };
  };
  /** Plnění slíbených termínů „na zakázku" (zatím jen CZ). */
  na_zakazku: {
    vcas: number;
    pozde: number;
    stornovano: number;
    ceka: number;
    v_prodleni: number;
    avg_days_early: number;
    avg_days_late: number;
  };
  /** Nehotové zakázky — hodnota, kusy a počet zakázek. */
  rozpracovano: { value_czk: number; pieces: number; orders: number };
  workers: WorkerRow[];
  worker_months: WorkerMonths[];
  categories: CategoryRow[];
};

/** Co se kreslí ve sloupcích — koruny, nebo kusy. */
type Metric = 'value' | 'qty';

const MONTHS_SHORT = [
  'led',
  'úno',
  'bře',
  'dub',
  'kvě',
  'čvn',
  'čvc',
  'srp',
  'zář',
  'říj',
  'lis',
  'pro',
];

/** Barvy sloupců podle stáří roku — nejnovější je naše zelená. */
const YEAR_COLORS = ['#c9d6e5', '#8fb3d9', '#4bbcff', '#8abe1a'];

/** 1 234 567 Kč — s mezerami po tisících, bez haléřů. */
function fmtCzk(n: number): string {
  return `${Math.round(n).toLocaleString('cs-CZ')} Kč`;
}

/** Osa Y — „2,5 mil." / „250 tis.". */
function fmtAxis(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toLocaleString('cs-CZ')} mil.`;
  if (n >= 1000) return `${Math.round(n / 1000)} tis.`;
  return String(n);
}

/** Rozdíl v procentech; null, když se nedá spočítat (loni nula). */
function pctChange(now: number, before: number): number | null {
  if (before === 0) return now === 0 ? 0 : null;
  return ((now - before) / before) * 100;
}

function pctClass(pct: number | null): string {
  if (pct === null) return 'text-text-muted';
  if (pct > 0) return 'text-success';
  if (pct < 0) return 'text-danger';
  return 'text-text-secondary';
}

function fmtPct(pct: number | null): string {
  if (pct === null) return '—';
  return `${pct > 0 ? '+' : ''}${pct.toFixed(1)} %`;
}

const RANGES = [2, 3, 4];

/** 'MM-DD' → '13. 8.' */
function fmtMD(md: string): string {
  const [m, d] = md.split('-');
  return m && d ? `${Number.parseInt(d, 10)}. ${Number.parseInt(m, 10)}.` : md;
}

/** Hodnota měsíce podle zvolené metriky. */
function metricValue(m: MonthRow | undefined, metric: Metric): number {
  return (metric === 'value' ? m?.value : m?.qty) ?? 0;
}

/** Hodnota kategorie podle metriky. */
function metricCat(c: CategoryRow, metric: Metric): number {
  return metric === 'value' ? c.value : c.qty;
}

/** Výkon (norma ÷ odšito) — stejné hranice jako ve výkazech. */
function perfClass(pct: number): string {
  if (pct >= 75) return 'text-success';
  if (pct >= 50) return 'text-warning';
  return 'text-danger';
}

function fmtMetric(n: number, metric: Metric): string {
  return metric === 'value' ? fmtCzk(n) : `${Math.round(n).toLocaleString('cs-CZ')} ks`;
}

type Tab = 'vyroba' | 'striharna' | 'dokoncovaci';

/** Stránka Statistiky — výroba (hodnota/kusy po měsících), střihárna a dokončovací dílna (zvlášť tab). */
export function StatistikyPage() {
  const [tab, setTab] = useState<Tab>('vyroba');
  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Statistiky výroby</h1>
      </div>
      <div className="border-b border-border mb-6">
        <nav className="flex gap-1 -mb-px">
          <TabButton active={tab === 'vyroba'} onClick={() => setTab('vyroba')}>
            Výroba
          </TabButton>
          <TabButton active={tab === 'striharna'} onClick={() => setTab('striharna')}>
            Střihárna
          </TabButton>
          <TabButton active={tab === 'dokoncovaci'} onClick={() => setTab('dokoncovaci')}>
            Dokončovací dílna
          </TabButton>
        </nav>
      </div>
      {tab === 'vyroba' ? (
        <VyrobaTab />
      ) : tab === 'striharna' ? (
        <StriharnaTab />
      ) : (
        <DokoncovaciTab />
      )}
    </>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const base = 'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer';
  const cls = active
    ? 'border-primary text-primary'
    : 'border-transparent text-text-secondary hover:text-text';
  return (
    <button type="button" onClick={onClick} className={`${base} ${cls}`}>
      {children}
    </button>
  );
}

/**
 * Výroba — hodnota vyrobeného zboží bez DPH. Osa X je leden až prosinec a
 * roky stojí vedle sebe, aby šlo porovnat sezónu se sezónou; pod grafem je
 * meziroční rozdíl v korunách i procentech.
 */
function VyrobaTab() {
  const [years, setYears] = useState(3);
  const [metric, setMetric] = useState<Metric>('value');
  const [data, setData] = useState<StatsData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    setData(null);
    apiFetch<StatsData>(`/api/vyroba/statistiky?years=${years}`)
      .then(setData)
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.describe() : 'Statistiky se nepodařilo načíst'),
      );
  }, [years]);

  const rows = data?.years ?? [];
  const current = rows[rows.length - 1];
  const previous = rows[rows.length - 2];
  const sum = (y: YearRow | undefined) =>
    (y?.months ?? []).reduce((s, m) => s + metricValue(m, metric), 0);
  // Srovnáváme od 1. 1. do dneška (přesně na den), ne celé měsíce — jinak by
  // loňský rok vždycky „vyhrál" jen díky zbytku aktuálního měsíce navíc.
  const ytdNow = data
    ? metric === 'value'
      ? data.ytd.this_year.value
      : data.ytd.this_year.qty
    : 0;
  const ytdPrev = data
    ? metric === 'value'
      ? data.ytd.last_year.value
      : data.ytd.last_year.qty
    : 0;

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <p className="text-sm text-text-secondary max-w-2xl">
          Hodnota vyrobeného zboží bez DPH po měsících. Zakázka se počítá do měsíce, kdy byla
          naskladněná. Rozpracovaná výroba jsou nedokončené zakázky.
        </p>
        <div className="flex gap-2 shrink-0">
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setYears(r)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors cursor-pointer ${
                years === r
                  ? 'bg-action text-white'
                  : 'bg-surface border border-border text-text-secondary hover:border-text-muted'
              }`}
            >
              {r} roky
            </button>
          ))}
        </div>
      </div>

      {err && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {err}
        </div>
      )}

      {data == null ? (
        <p className="text-sm text-text-muted px-1">Načítám…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-5">
            <Tile
              label="Rozpracovaná výroba"
              value={fmtCzk(data.rozpracovano.value_czk)}
              note={`${data.rozpracovano.orders} zakázek · ${data.rozpracovano.pieces} ks`}
              valueClass="text-primary"
            />
            <Tile
              label={`Letos (${current?.year ?? ''})`}
              value={fmtMetric(sum(current), metric)}
              note="celý rok zatím"
            />
            <Tile
              label={`Loni (${previous?.year ?? ''})`}
              value={fmtMetric(sum(previous), metric)}
              note="celý rok"
            />
            <Tile
              label="Stejné období loni"
              value={fmtMetric(ytdPrev, metric)}
              note={data ? `1. 1.–${fmtMD(data.ytd.through)}` : '—'}
            />
            <Tile
              label="Meziročně"
              value={fmtPct(pctChange(ytdNow, ytdPrev))}
              note={`${fmtMetric(ytdNow - ytdPrev, metric)} proti loňsku`}
              valueClass={pctClass(pctChange(ytdNow, ytdPrev))}
            />
          </div>

          <NaZakazkuCard data={data.na_zakazku} />

          <div className="bg-surface rounded-xl shadow-card px-5 py-5 mb-5">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                {metric === 'value' ? 'Hodnota vyrobeného zboží bez DPH' : 'Vyrobené kusy'}
              </h2>
              <div className="flex gap-1">
                {(['value', 'qty'] as Metric[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMetric(m)}
                    className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${
                      metric === m
                        ? 'bg-action text-white'
                        : 'bg-bg border border-border text-text-secondary hover:border-text-muted'
                    }`}
                  >
                    {m === 'value' ? 'Koruny' : 'Kusy'}
                  </button>
                ))}
              </div>
              <div className="flex gap-4 text-sm">
                {rows.map((y, i) => (
                  <span key={y.year} className="flex items-center gap-1.5">
                    <span
                      className="inline-block h-3 w-3 rounded-sm"
                      style={{ backgroundColor: color(i, rows.length) }}
                    />
                    <span className="text-text-secondary">{y.year}</span>
                  </span>
                ))}
              </div>
            </div>
            <Chart rows={rows} metric={metric} hover={hover} onHover={setHover} />
          </div>

          <div className="bg-surface rounded-xl shadow-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                  <th className="px-4 py-2.5">Měsíc</th>
                  {rows.map((y) => (
                    <th key={y.year} className="px-4 py-2.5 text-right">
                      {y.year}
                    </th>
                  ))}
                  {previous && current && (
                    <>
                      <th className="px-4 py-2.5 text-right">
                        Rozdíl {current.year}/{previous.year}
                      </th>
                      <th className="px-4 py-2.5 text-right">%</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {MONTHS_SHORT.map((_, i) => {
                  const now = metricValue(current?.months[i], metric);
                  const before = metricValue(previous?.months[i], metric);
                  const pct = pctChange(now, before);
                  return (
                    <tr
                      key={MONTHS_SHORT[i]}
                      className={`border-b border-border-light ${hover === i ? 'bg-primary-25' : ''}`}
                      onMouseEnter={() => setHover(i)}
                      onMouseLeave={() => setHover(null)}
                    >
                      <td className="px-4 py-1.5 text-text capitalize">
                        {current?.months[i]?.label.split(' ')[0] ?? MONTHS_SHORT[i]}
                      </td>
                      {rows.map((y) => (
                        <td key={y.year} className="px-4 py-1.5 text-right text-text-secondary">
                          {metricValue(y.months[i], metric)
                            ? fmtMetric(metricValue(y.months[i], metric), metric)
                            : '—'}
                        </td>
                      ))}
                      {previous && current && (
                        <>
                          <td
                            className={`px-4 py-1.5 text-right font-semibold ${now || before ? pctClass(pct) : 'text-text-muted'}`}
                          >
                            {now || before ? fmtMetric(now - before, metric) : '—'}
                          </td>
                          <td className={`px-4 py-1.5 text-right font-semibold ${pctClass(pct)}`}>
                            {now || before ? fmtPct(pct) : '—'}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
                <tr className="bg-bg">
                  <td className="px-4 py-2 font-bold text-text">Celkem</td>
                  {rows.map((y) => (
                    <td key={y.year} className="px-4 py-2 text-right font-bold text-text">
                      {fmtMetric(sum(y), metric)}
                    </td>
                  ))}
                  {previous && current && (
                    <>
                      <td className="px-4 py-2 text-right font-bold text-text">
                        {fmtMetric(sum(current) - sum(previous), metric)}
                      </td>
                      <td
                        className={`px-4 py-2 text-right font-bold ${pctClass(pctChange(sum(current), sum(previous)))}`}
                      >
                        {fmtPct(pctChange(sum(current), sum(previous)))}
                      </td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Naskladněno po dnech (30 dní) ────────────────────────────── */}
          <div className="mt-5 bg-surface rounded-xl shadow-card px-5 py-5">
            <h2 className="text-sm font-bold text-text uppercase tracking-wide mb-4">
              Hodnota naskladněného zboží bez DPH — posledních 30 dní
            </h2>
            <AreaChart
              points={data.daily.map((d) => ({ label: fmtDay(d.date), value: d.value }))}
              everyNth={3}
            />
          </div>

          {/* ── Za kolik která švadlenka vyrobila ────────────────────────── */}
          {data.worker_months.length > 0 && (
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
              {data.worker_months.map((w) => (
                <div key={w.worker} className="bg-surface rounded-xl shadow-card px-5 py-5">
                  <h2 className="text-sm font-bold text-text uppercase tracking-wide mb-1">
                    {w.worker} — za kolik vyrobila
                  </h2>
                  <div className="text-xs text-text-muted mb-3">
                    Prodejní cena bez DPH · celkem{' '}
                    {fmtCzk(w.months.reduce((s, m) => s + m.value, 0))}
                  </div>
                  <AreaChart
                    points={w.months.map((m) => ({ label: fmtMonth(m.month), value: m.value }))}
                    everyNth={2}
                    scale={1.7}
                  />
                </div>
              ))}
            </div>
          )}
          {/* ── Rozpad podle kategorií ───────────────────────────────────── */}
          <div className="mt-5 bg-surface rounded-xl shadow-card px-5 py-5">
            <h2 className="text-sm font-bold text-text uppercase tracking-wide mb-4">
              Podle kategorií — {data.year}
            </h2>
            {data.categories.length === 0 ? (
              <p className="text-sm text-text-muted">V tomhle roce zatím nic vyrobeného.</p>
            ) : (
              <div className="space-y-1.5">
                {data.categories.map((cat) => {
                  const max = Math.max(...data.categories.map((x) => metricCat(x, metric)));
                  const v = metricCat(cat, metric);
                  return (
                    <div key={cat.category} className="flex items-center gap-3">
                      <span
                        className="w-64 shrink-0 truncate text-sm text-text"
                        title={cat.category}
                      >
                        {cat.category}
                      </span>
                      <span className="flex-1 h-5 rounded bg-bg overflow-hidden">
                        <span
                          className="block h-full rounded bg-action"
                          style={{ width: `${max > 0 ? (v / max) * 100 : 0}%` }}
                        />
                      </span>
                      <span className="w-28 shrink-0 text-right text-sm font-semibold text-text">
                        {fmtMetric(v, metric)}
                      </span>
                      <span className="w-24 shrink-0 text-right text-xs text-text-muted">
                        {cat.orders} zak.
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Srovnání švadlen ─────────────────────────────────────────── */}
          <div className="mt-5 bg-surface rounded-xl shadow-card overflow-x-auto">
            <div className="px-5 py-4">
              <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                Podle švadlen — {data.year}
              </h2>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                  <th className="px-4 py-2.5">Švadlenka</th>
                  <th className="px-4 py-2.5 text-right">Zakázek</th>
                  <th className="px-4 py-2.5 text-right">Kusů</th>
                  <th className="px-4 py-2.5 text-right">Hodnota</th>
                  <th className="px-4 py-2.5 text-right">Norma (h)</th>
                  <th className="px-4 py-2.5 text-right">Odšito (h)</th>
                  <th className="px-4 py-2.5 text-right">Výkon</th>
                  <th className="px-4 py-2.5 text-right">Chyb</th>
                  <th className="px-4 py-2.5 text-right">Chybovost</th>
                </tr>
              </thead>
              <tbody>
                {data.workers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-text-muted">
                      Za tenhle rok nemáme dokončené zakázky se švadlenkou.
                    </td>
                  </tr>
                ) : (
                  data.workers.map((w) => (
                    <tr key={w.worker} className="border-b border-border-light">
                      <td className="px-4 py-1.5 font-semibold text-text">{w.worker}</td>
                      <td className="px-4 py-1.5 text-right text-text-secondary">{w.orders}</td>
                      <td className="px-4 py-1.5 text-right text-text">{w.qty}</td>
                      <td className="px-4 py-1.5 text-right text-text-secondary">
                        {fmtCzk(w.value)}
                      </td>
                      <td className="px-4 py-1.5 text-right text-text-secondary">
                        {w.norm_hours.toFixed(1)}
                      </td>
                      <td className="px-4 py-1.5 text-right text-text-secondary">
                        {w.sewn_hours.toFixed(1)}
                      </td>
                      <td
                        className={`px-4 py-1.5 text-right font-semibold ${perfClass(w.performance)}`}
                      >
                        {w.performance.toFixed(1)} %
                      </td>
                      <td className="px-4 py-1.5 text-right text-text-secondary">{w.errors}</td>
                      <td className="px-4 py-1.5 text-right text-text-secondary">
                        {w.qty > 0 ? `${((w.errors / w.qty) * 100).toFixed(1)} %` : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}

type StriharnaPerson = {
  person: string;
  total_hours: number;
  daily: { date: string; hours: number }[];
};

type StriharnaStats = {
  since: string;
  totals: { hours: number; qty: number; records: number; unmatched: number };
  daily: { date: string; hours: number }[];
  by_person: StriharnaPerson[];
};

/** '1 234 ks' — s mezerami po tisících. */
function fmtKs(n: number): string {
  return `${Math.round(n).toLocaleString('cs-CZ')} ks`;
}

/** '12,5 h' — čas stříhání zaokrouhlený na desetiny hodiny. */
function fmtHod(n: number): string {
  return `${n.toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} h`;
}

/** 'YYYY-MM-DD' → '3. 1. 2024' — s rokem, protože starší data sahají přes rok. */
function fmtDayYear(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${Number(d)}. ${Number(m)}. ${y}` : iso;
}

/** 'YYYY-MM' dnešního měsíce. */
function currentMonthKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Sečte denní řadu po měsících v rozsahu [fromMonth, toMonth] (obojí 'YYYY-MM', včetně). */
function monthlyFromDaily(
  daily: { date: string; hours: number }[],
  fromMonth: string,
  toMonth: string,
): { month: string; hours: number }[] {
  const sums = new Map<string, number>();
  for (const d of daily) {
    const key = d.date.slice(0, 7);
    if (key < fromMonth || key > toMonth) continue;
    sums.set(key, (sums.get(key) ?? 0) + d.hours);
  }
  const out: { month: string; hours: number }[] = [];
  const fromParts = fromMonth.split('-').map(Number);
  const toParts = toMonth.split('-').map(Number);
  let y = fromParts[0] ?? new Date().getFullYear();
  let m = fromParts[1] ?? 1;
  const toY = toParts[0] ?? y;
  const toM = toParts[1] ?? 12;
  while (y < toY || (y === toY && m <= toM)) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    out.push({ month: key, hours: Math.round((sums.get(key) ?? 0) * 100) / 100 });
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

/** Posledních `n` dnů (dnešek včetně), doplněné nulami pro dny bez záznamu. */
function lastDaysFromDaily(
  daily: { date: string; hours: number }[],
  n: number,
): { date: string; hours: number }[] {
  const sums = new Map(daily.map((d) => [d.date, d.hours]));
  const out: { date: string; hours: number }[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;
    out.push({ date: key, hours: Math.round((sums.get(key) ?? 0) * 100) / 100 });
  }
  return out;
}

/** Všechny dny zadaného měsíce ('YYYY-MM'), doplněné nulami pro dny bez záznamu. */
function daysInMonthFromDaily(
  daily: { date: string; hours: number }[],
  month: string,
): { date: string; hours: number }[] {
  const sums = new Map(daily.map((d) => [d.date, d.hours]));
  const [y, m] = month.split('-').map(Number);
  const daysCount = y && m ? new Date(y, m, 0).getDate() : 0;
  const out: { date: string; hours: number }[] = [];
  for (let day = 1; day <= daysCount; day++) {
    const key = `${month}-${String(day).padStart(2, '0')}`;
    out.push({ date: key, hours: Math.round((sums.get(key) ?? 0) * 100) / 100 });
  }
  return out;
}

/**
 * Střihárna — čas stříhání = kusy zakázky × norma kategorie (uložené přímo na
 * zakázce, viz `computeCuttingNorm`). Datum je datum vzniku zakázky (audit
 * log), proto žádný výběr rozsahu let — data začínají tím, odkdy audit log
 * datum vzniku eviduje.
 */
function StriharnaTab() {
  const [data, setData] = useState<StriharnaStats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Rozsah hlavního grafu „po měsících" — defaultně leden aktuálního roku
  // až dnešní měsíc, uživatel si ho může zúžit/rozšířit (i mimo tenhle rok).
  const [monthFrom, setMonthFrom] = useState(() => `${new Date().getFullYear()}-01`);
  const [monthTo, setMonthTo] = useState(() => currentMonthKey());
  // Prázdné = přehled po měsících (výchozí); vyplněné = rozpad po dnech
  // zvoleného měsíce, u grafu „po osobách".
  const [personMonth, setPersonMonth] = useState('');

  useEffect(() => {
    apiFetch<StriharnaStats>('/api/vyroba/statistiky/striharna')
      .then(setData)
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.describe() : 'Statistiky se nepodařilo načíst'),
      );
  }, []);

  if (err) {
    return (
      <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3">
        {err}
      </div>
    );
  }
  if (data == null) {
    return <p className="text-sm text-text-muted px-1">Načítám…</p>;
  }

  // Graf „po osobách": bez vybraného měsíce přehled po měsících (od března —
  // dřív se latka/naplet nevyplňovalo); klik na sloupec měsíce rozkreslí jeho
  // jednotlivé dny (1. až poslední den toho měsíce).
  const personMonthKeys = monthlyFromDaily(
    data.daily,
    `${new Date().getFullYear()}-03`,
    currentMonthKey(),
  ).map((m) => m.month);
  const personLabels = personMonth
    ? daysInMonthFromDaily(data.daily, personMonth).map((d) => fmtDay(d.date))
    : personMonthKeys.map((key) => MONTHS_SHORT[Number(key.slice(5, 7)) - 1] ?? key);
  const personSeries = data.by_person.map((p) => ({
    person: p.person,
    values: personMonth
      ? daysInMonthFromDaily(p.daily, personMonth).map((d) => d.hours)
      : monthlyFromDaily(p.daily, `${new Date().getFullYear()}-03`, currentMonthKey()).map(
          (m) => m.hours,
        ),
  }));

  return (
    <>
      <p className="text-sm text-text-secondary max-w-2xl mb-6">
        Čas stříhání zakázek podle normy kategorie (kusy × minuty na kus). U zakázek založených po
        2. 8. 2026 je to datum vzniku, u starších přiblížení podle data naskladnění/konce šití.
        Evidujeme od {fmtDayYear(data.since)}.
        {data.totals.unmatched > 0 &&
          ` ${data.totals.unmatched} zakázek vynecháno (chybí norma kategorie nebo žádné datum).`}
      </p>

      {data.totals.records === 0 ? (
        <p className="text-sm text-text-muted px-1">Zatím žádná zakázka se spočítanou normou.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            <Tile
              label="Celkem stříhání"
              value={fmtHod(data.totals.hours)}
              note={`${data.totals.records} zakázek`}
              valueClass="text-primary"
            />
            <Tile label="Nastříháno kusů" value={fmtKs(data.totals.qty)} note="součet celemKs" />
            <Tile
              label="Průměr na zakázku"
              value={
                data.totals.records > 0 ? fmtHod(data.totals.hours / data.totals.records) : '—'
              }
              note="čas stříhání"
            />
          </div>

          <div className="bg-surface rounded-xl shadow-card px-5 py-5 mb-5">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                Čas stříhání po měsících
              </h2>
              <div className="flex gap-3">
                <label className="w-40 h-16 rounded-xl border-2 border-border px-4 flex flex-col justify-center cursor-pointer">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                    Od měsíce
                  </span>
                  <input
                    type="month"
                    value={monthFrom}
                    onChange={(e) => setMonthFrom(e.target.value)}
                    className="bg-transparent border-0 p-0 text-sm font-bold text-text focus:outline-none w-full"
                  />
                </label>
                <label className="w-40 h-16 rounded-xl border-2 border-border px-4 flex flex-col justify-center cursor-pointer">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                    Do měsíce
                  </span>
                  <input
                    type="month"
                    value={monthTo}
                    onChange={(e) => setMonthTo(e.target.value)}
                    className="bg-transparent border-0 p-0 text-sm font-bold text-text focus:outline-none w-full"
                  />
                </label>
              </div>
            </div>
            <AreaChart
              points={monthlyFromDaily(data.daily, monthFrom, monthTo).map((m) => ({
                label: fmtMonth(m.month),
                value: m.hours,
              }))}
              everyNth={1}
              fmtValue={fmtHod}
              emptyLabel="nic nastříháno"
            />
          </div>

          {data.by_person.length > 0 && (
            <>
              <div className="bg-surface rounded-xl shadow-card px-5 py-5 mb-5">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                  <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                    Čas stříhání po osobách
                    {personMonth && (
                      <span className="text-text-muted normal-case font-normal">
                        {' '}
                        — {fmtMonth(personMonth)}
                      </span>
                    )}
                  </h2>
                  {personMonth && (
                    <button
                      type="button"
                      onClick={() => setPersonMonth('')}
                      className="rounded-lg bg-action hover:bg-action-dark text-white text-xs font-bold px-4 py-2 cursor-pointer transition-colors"
                    >
                      Vrátit na celkový přehled
                    </button>
                  )}
                </div>
                <PersonBarChart
                  labels={personLabels}
                  series={personSeries}
                  title="Čas stříhání po osobách a měsících"
                  onSelectIndex={
                    personMonth ? undefined : (i) => setPersonMonth(personMonthKeys[i] ?? '')
                  }
                />
              </div>

              <div className="bg-surface rounded-xl shadow-card px-5 py-5 mb-5">
                <h2 className="text-sm font-bold text-text uppercase tracking-wide mb-4">
                  Čas stříhání po osobách — posledních 14 dní
                </h2>
                <PersonBarChart
                  labels={lastDaysFromDaily(data.daily, 14).map((d) => fmtDay(d.date))}
                  series={data.by_person.map((p) => ({
                    person: p.person,
                    values: lastDaysFromDaily(p.daily, 14).map((d) => d.hours),
                  }))}
                  title="Čas stříhání po osobách a dnech"
                />
              </div>
            </>
          )}

          <StriharnaKontrola people={data.by_person.map((p) => p.person)} />
        </>
      )}
    </>
  );
}

type StriharnaDetailRow = {
  id_zakazky: string;
  product_code: string;
  product_name: string;
  qty: number;
  latka: string | null;
  latka_hours: number | null;
  naplet: string | null;
  naplet_hours: number | null;
};

/**
 * Kontrola podle dne a osoby — pro audit: „souhlasí to, co graf ukazuje, se
 * skutečnými zakázkami?". Stejná logika dne jako grafy (`resolveOrderDay` na
 * backendu), ať sedí 1:1.
 */
function StriharnaKontrola({ people }: { people: string[] }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [person, setPerson] = useState('');
  const [rows, setRows] = useState<StriharnaDetailRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!date) {
      setRows(null);
      return;
    }
    setErr(null);
    apiFetch<{ rows: StriharnaDetailRow[] }>(
      `/api/vyroba/statistiky/striharna/detail?date=${date}${
        person ? `&person=${encodeURIComponent(person)}` : ''
      }`,
    )
      .then((d) => setRows(d.rows))
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.describe() : 'Chyba načítání'));
  }, [date, person]);

  // Bez vybrané osoby počítáme oba díly (látku i náplet) — je to celkový čas
  // stříhání toho dne. S vybranou osobou jen její díl, ať sedí na jméno.
  const totalHours = (rows ?? []).reduce((sum, r) => {
    let t = 0;
    if (!person || r.latka === person) t += r.latka_hours ?? 0;
    if (!person || r.naplet === person) t += r.naplet_hours ?? 0;
    return sum + t;
  }, 0);

  return (
    <div className="bg-surface rounded-xl shadow-card px-5 py-5 mt-5">
      <h2 className="text-sm font-bold text-text uppercase tracking-wide mb-1">
        Kontrola podle dne a osoby
      </h2>
      <p className="text-xs text-text-muted mb-4">
        Zakázky přiřazené k vybranému dni (stejná logika jako grafy výš) — kdo stříhal látku a kdo
        náplet a kolik to zabralo času.
      </p>
      <div className="flex flex-wrap gap-3 mb-4">
        <label className="w-40 h-16 rounded-xl border-2 border-border px-4 flex flex-col justify-center cursor-pointer">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
            Datum
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-transparent border-0 p-0 text-sm font-bold text-text focus:outline-none w-full"
          />
        </label>
        <label className="w-40 h-16 rounded-xl border-2 border-border px-4 flex flex-col justify-center cursor-pointer">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
            Osoba
          </span>
          <select
            value={person}
            onChange={(e) => setPerson(e.target.value)}
            className="bg-transparent border-0 p-0 text-sm font-bold text-text focus:outline-none w-full cursor-pointer"
          >
            <option value="">Všichni</option>
            {people.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        <div className="w-40 h-16 rounded-xl border-2 border-primary/30 bg-primary-25 px-4 flex flex-col justify-center items-center">
          <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
            Celkem hodin
          </div>
          <div className="text-lg font-bold text-text mt-0.5">{fmtHod(totalHours)}</div>
        </div>
      </div>

      {err && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {err}
        </div>
      )}

      {rows == null ? (
        <p className="text-sm text-text-muted">Načítám…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-text-muted">Pro tenhle den (a osobu) nic nemáme.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                <th className="px-4 py-2.5">Zakázka</th>
                <th className="px-4 py-2.5">Produkt</th>
                <th className="px-4 py-2.5 text-right">Kusů</th>
                <th className="px-4 py-2.5">Látka</th>
                <th className="px-4 py-2.5 text-right">Hodin (látka)</th>
                <th className="px-4 py-2.5">Náplet</th>
                <th className="px-4 py-2.5 text-right">Hodin (náplet)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id_zakazky} className="border-b border-border-light">
                  <td className="px-4 py-1.5 font-mono text-text-muted">{r.id_zakazky}</td>
                  <td className="px-4 py-1.5 text-text">
                    <span className="font-mono text-text-muted mr-2">{r.product_code}</span>
                    {r.product_name}
                  </td>
                  <td className="px-4 py-1.5 text-right text-text-secondary">{fmtKs(r.qty)}</td>
                  <td className="px-4 py-1.5 text-text">{r.latka ?? '—'}</td>
                  <td className="px-4 py-1.5 text-right text-text-secondary">
                    {r.latka_hours != null ? fmtHod(r.latka_hours) : '—'}
                  </td>
                  <td className="px-4 py-1.5 text-text">{r.naplet ?? '—'}</td>
                  <td className="px-4 py-1.5 text-right text-text-secondary">
                    {r.naplet_hours != null ? fmtHod(r.naplet_hours) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

type DokoncovaciStats = {
  since: string;
  totals: { hours: number; qty: number; records: number; unmatched: number; externi: number };
  daily: { date: string; hours: number }[];
  categories: { category: string; norm_sec: number | null }[];
};

/** '130' vteřin → '2 min 10 s'. */
function fmtVterinyNaCasZ(sec: number): string {
  const min = Math.floor(sec / 60);
  const zb = Math.round(sec % 60);
  if (min === 0) return `${zb} s`;
  return zb === 0 ? `${min} min` : `${min} min ${zb} s`;
}

/**
 * Dokončovací dílna — čas = kusy zakázky × AKTUÁLNÍ norma kategorie (vteřiny,
 * `normaDokoncovaciDilnaBezOstrihavani`). Na rozdíl od střihárny se nepočítá
 * a neukládá na zakázku, ale živě při každém dotazu — ať vždycky odpovídá
 * aktuální normě v Databázi kategorií. Pod grafem je výpis VŠECH kategorií a
 * jejich normy, ať jde ověřit, že číslo dává smysl (a případně ho upravit).
 */
function DokoncovaciTab() {
  const [data, setData] = useState<DokoncovaciStats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [monthFrom, setMonthFrom] = useState(() => `${new Date().getFullYear()}-01`);
  const [monthTo, setMonthTo] = useState(() => currentMonthKey());

  useEffect(() => {
    apiFetch<DokoncovaciStats>('/api/vyroba/statistiky/dokoncovaci-dilna')
      .then(setData)
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.describe() : 'Statistiky se nepodařilo načíst'),
      );
  }, []);

  if (err) {
    return (
      <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3">
        {err}
      </div>
    );
  }
  if (data == null) {
    return <p className="text-sm text-text-muted px-1">Načítám…</p>;
  }

  const bezNormy = data.categories.filter((c) => c.norm_sec == null).length;

  return (
    <>
      <p className="text-sm text-text-secondary max-w-2xl mb-6">
        Čas dokončovací dílny (bez ostřihávání) podle AKTUÁLNÍ normy kategorie (kusy × vteřiny na
        kus) — počítá se živě, ne uloženě na zakázce jako u střihárny, takže vždycky odpovídá tomu,
        co je zrovna nastavené v Databázi kategorií. Externí dílna (Mamka) se do grafů nepočítá —
        řeší si dokončení sama. Evidujeme od {fmtDayYear(data.since)}.
        {data.totals.unmatched > 0 &&
          ` ${data.totals.unmatched} zakázek vynecháno (chybí norma kategorie nebo žádné datum).`}
      </p>

      {data.totals.records === 0 ? (
        <p className="text-sm text-text-muted px-1">Zatím žádná zakázka se spočítanou normou.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
            <Tile
              label="Celkem dokončovací dílna"
              value={fmtHod(data.totals.hours)}
              note={`${data.totals.records} zakázek`}
              valueClass="text-primary"
            />
            <Tile label="Zpracováno kusů" value={fmtKs(data.totals.qty)} note="součet celemKs" />
            <Tile
              label="Průměr na zakázku"
              value={
                data.totals.records > 0 ? fmtHod(data.totals.hours / data.totals.records) : '—'
              }
              note="čas dokončovací dílny"
            />
          </div>

          <div className="bg-surface rounded-xl shadow-card px-5 py-5 mb-5">
            <h2 className="text-sm font-bold text-text uppercase tracking-wide mb-4">
              Čas dokončovací dílny po dnech — posledních 14 dní
            </h2>
            <AreaChart
              points={lastDaysFromDaily(data.daily, 14).map((d) => ({
                label: fmtDay(d.date),
                value: d.hours,
              }))}
              everyNth={1}
              fmtValue={fmtHod}
              emptyLabel="nic dokončováno"
            />
          </div>

          <div className="bg-surface rounded-xl shadow-card px-5 py-5 mb-5">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <h2 className="text-sm font-bold text-text uppercase tracking-wide">
                Čas dokončovací dílny po měsících
              </h2>
              <div className="flex gap-3">
                <label className="w-40 h-16 rounded-xl border-2 border-border px-4 flex flex-col justify-center cursor-pointer">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                    Od měsíce
                  </span>
                  <input
                    type="month"
                    value={monthFrom}
                    onChange={(e) => setMonthFrom(e.target.value)}
                    className="bg-transparent border-0 p-0 text-sm font-bold text-text focus:outline-none w-full"
                  />
                </label>
                <label className="w-40 h-16 rounded-xl border-2 border-border px-4 flex flex-col justify-center cursor-pointer">
                  <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                    Do měsíce
                  </span>
                  <input
                    type="month"
                    value={monthTo}
                    onChange={(e) => setMonthTo(e.target.value)}
                    className="bg-transparent border-0 p-0 text-sm font-bold text-text focus:outline-none w-full"
                  />
                </label>
              </div>
            </div>
            <AreaChart
              points={monthlyFromDaily(data.daily, monthFrom, monthTo).map((m) => ({
                label: fmtMonth(m.month),
                value: m.hours,
              }))}
              everyNth={1}
              fmtValue={fmtHod}
              emptyLabel="nic dokončováno"
            />
          </div>
        </>
      )}

      <div className="bg-surface rounded-xl shadow-card px-5 py-5 mb-5">
        <h2 className="text-sm font-bold text-text uppercase tracking-wide mb-1">
          Normy kategorií — dokončovací dílna (bez ostřihávání)
        </h2>
        <p className="text-xs text-text-muted mb-4">
          Všechny kategorie produktů a jejich aktuální norma, ať jde ověřit, že číslo dává smysl —
          upravuje se na úrovni kategorie v Databázi kategorií.
          {bezNormy > 0 &&
            ` ${bezNormy} kategorií nemá normu vyplněnou (zakázky se do grafu výš nepočítají).`}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                <th className="px-4 py-2.5">Kategorie</th>
                <th className="px-4 py-2.5 text-right">Norma (vteřin)</th>
                <th className="px-4 py-2.5 text-right">Norma (čas)</th>
              </tr>
            </thead>
            <tbody>
              {data.categories.map((c) => (
                <tr key={c.category} className="border-b border-border-light">
                  <td className="px-4 py-1.5 text-text">{c.category}</td>
                  <td className="px-4 py-1.5 text-right text-text-secondary">
                    {c.norm_sec ?? '—'}
                  </td>
                  <td className="px-4 py-1.5 text-right text-text-secondary">
                    {c.norm_sec != null ? fmtVterinyNaCasZ(c.norm_sec) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/** Barvy sloupců pro jednotlivé osoby — cyklí, kdyby přibyl čtvrtý člověk. */
const PERSON_COLORS = ['#8abe1a', '#4bbcff', '#f59e0b', '#ec1268'];

/**
 * Skupinový sloupcový graf — jeden sloupec na osobu v každém úseku (měsíc,
 * nebo den). Vlastní jednodušší varianta `Chart` (ta je vázaná na roky/metriku
 * Kč-ks výroby). `labelEveryNth` skryje popisky mezi sloupci u hustších řad
 * (dny), ať se text neslije — sloupce samotné se kreslí všechny.
 */
function PersonBarChart({
  labels,
  series,
  labelEveryNth = 1,
  title = 'Čas stříhání po osobách',
  onSelectIndex,
}: {
  labels: string[];
  series: { person: string; values: number[] }[];
  labelEveryNth?: number;
  title?: string;
  /** Zadané → sloupce jsou klikací (např. rozkliknutí měsíce na jeho dny). */
  onSelectIndex?: (i: number) => void;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 1000;
  const H = 340;
  const padL = 64;
  const padB = 30;
  const padT = 16;
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const step = niceStep(max / 5);
  const top = Math.ceil(max / step) * step;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const n = Math.max(1, labels.length);
  const gw = (W - padL - 10) / n;
  const bw = (gw * 0.8) / Math.max(1, series.length);

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full min-w-[680px] h-auto"
        role="img"
        aria-label={title}
      >
        <title>{title}</title>
        {ticks.map((t) => {
          const y = H - padB - ((H - padB - padT) * t) / top;
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - 10} y2={y} stroke="#e5e7eb" strokeWidth="1" />
              <text x={padL - 8} y={y + 4} fontSize="12" textAnchor="end" fill="#6b7280">
                {fmtHod(t)}
              </text>
            </g>
          );
        })}
        {labels.map((label, i) => {
          const gx = padL + i * gw;
          const active = hover === i;
          return (
            <g
              key={label}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onClick={onSelectIndex ? () => onSelectIndex(i) : undefined}
              onKeyDown={
                onSelectIndex
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') onSelectIndex(i);
                    }
                  : undefined
              }
              role={onSelectIndex ? 'button' : undefined}
              tabIndex={onSelectIndex ? 0 : undefined}
              style={onSelectIndex ? { cursor: 'pointer' } : undefined}
            >
              <rect
                x={gx}
                y={padT}
                width={gw}
                height={H - padB - padT}
                fill={active ? '#f6fcff' : 'transparent'}
              />
              {series.map((s, si) => {
                const v = s.values[i] ?? 0;
                const h = ((H - padB - padT) * v) / top;
                const x = gx + gw * 0.1 + si * bw;
                return v > 0 ? (
                  <rect
                    key={s.person}
                    x={x}
                    y={H - padB - h}
                    width={bw * 0.9}
                    height={h}
                    rx="2"
                    fill={PERSON_COLORS[si % PERSON_COLORS.length]}
                  />
                ) : null;
              })}
              {i % labelEveryNth === 0 && (
                <text
                  x={gx + gw / 2}
                  y={H - padB + 16}
                  fontSize="11"
                  textAnchor="middle"
                  fill={active ? '#374151' : '#6b7280'}
                  fontWeight={active ? 'bold' : 'normal'}
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}
        <line x1={padL} y1={H - padB} x2={W - 10} y2={H - padB} stroke="#9ca3af" strokeWidth="1" />
        {hover !== null && (
          <g pointerEvents="none">
            {(() => {
              const boxW = 150;
              const lineH = 16;
              const boxH = 12 + series.length * lineH;
              const center = padL + hover * gw + gw / 2;
              const x = Math.min(Math.max(center - boxW / 2, 4), W - boxW - 4);
              const y = 8;
              return (
                <>
                  <rect
                    x={x}
                    y={y}
                    width={boxW}
                    height={boxH}
                    rx="6"
                    fill="#fff"
                    stroke="#cbd5e1"
                  />
                  {series.map((s, si) => (
                    <text
                      key={s.person}
                      x={x + 10}
                      y={y + 16 + si * lineH}
                      fontSize="11"
                      fill="#374151"
                    >
                      {s.person}: {fmtHod(s.values[hover ?? 0] ?? 0)}
                    </text>
                  ))}
                </>
              );
            })()}
          </g>
        )}
      </svg>
      <div className="flex gap-4 text-sm mt-2 justify-center">
        {series.map((s, si) => (
          <span key={s.person} className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-sm"
              style={{ backgroundColor: PERSON_COLORS[si % PERSON_COLORS.length] }}
            />
            <span className="text-text-secondary">{s.person}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Nejnovější rok dostane naši zelenou, starší modré odstíny. */
function color(index: number, count: number): string {
  if (index === count - 1) return YEAR_COLORS[YEAR_COLORS.length - 1] as string;
  const shades = YEAR_COLORS.slice(0, -1);
  return shades[Math.max(0, shades.length - (count - 1 - index))] ?? (shades[0] as string);
}

function Tile({
  label,
  value,
  note,
  valueClass = 'text-text',
  onClick,
}: {
  label: string;
  value: string;
  note: string;
  valueClass?: string;
  /** Zadané → dlaždice je klikací (kulaté tlačítko, ne jen div). */
  onClick?: () => void;
}) {
  const content = (
    <>
      <div className="text-xs font-semibold text-text-muted uppercase tracking-wide">{label}</div>
      <div className={`text-xl font-bold mt-1 ${valueClass}`}>{value}</div>
      <div className="text-xs text-text-muted mt-0.5">{note}</div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="bg-surface rounded-xl shadow-card px-5 py-4 text-left w-full cursor-pointer hover:ring-2 hover:ring-primary/30 transition-shadow"
      >
        {content}
      </button>
    );
  }
  return <div className="bg-surface rounded-xl shadow-card px-5 py-4">{content}</div>;
}

/**
 * Plnění slíbených termínů „na zakázku" — slib = datum objednání + přislíbený
 * počet dní, splnění = přechod objednávky do stavu „Odesláno". Zatím jen CZ.
 */
type ProdleniOrder = {
  order_code: string;
  deadline: string;
  days_late: number;
  customer_email: string | null;
  total_with_vat: number | null;
  currency_code: string | null;
  shoptet_url: string;
};

/** '3 dny' / '1 den' / '5 dní' — české skloňování počtu dní. */
function fmtDny(n: number): string {
  if (n === 1) return '1 den';
  if (n >= 2 && n <= 4) return `${n} dny`;
  return `${n} dní`;
}

function NaZakazkuCard({ data }: { data: StatsData['na_zakazku'] }) {
  const resolved = data.vcas + data.pozde;
  const celkem = resolved + data.stornovano + data.ceka;
  const [prodleniOpen, setProdleniOpen] = useState(false);
  const [prodleniOrders, setProdleniOrders] = useState<ProdleniOrder[] | null>(null);
  const [prodleniErr, setProdleniErr] = useState<string | null>(null);

  async function toggleProdleni() {
    const next = !prodleniOpen;
    setProdleniOpen(next);
    if (next && prodleniOrders === null) {
      try {
        const d = await apiFetch<{ orders: ProdleniOrder[] }>(
          '/api/vyroba/statistiky/na-zakazku-v-prodleni',
        );
        setProdleniOrders(d.orders);
      } catch (e: unknown) {
        setProdleniErr(e instanceof ApiError ? e.describe() : 'Chyba načítání');
      }
    }
  }

  if (celkem === 0) return null;
  const pctVcas = resolved > 0 ? (data.vcas / resolved) * 100 : null;
  const pctStorno = celkem > 0 ? (data.stornovano / celkem) * 100 : 0;

  return (
    <div className="mb-5">
      <h2 className="text-sm font-bold text-text uppercase tracking-wide mb-3">
        Na zakázku — plnění slíbených termínů (CZ)
      </h2>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Tile
          label="V termínu"
          value={pctVcas === null ? '—' : `${pctVcas.toFixed(0)} %`}
          note={`${data.vcas} z ${resolved} vyřízených`}
          valueClass={pctVcas === null ? 'text-text' : perfClass(pctVcas)}
        />
        <Tile
          label="Průměr — dřív"
          value={data.vcas > 0 ? `${data.avg_days_early.toFixed(1)} dní` : '—'}
          note="u zakázek v termínu"
          valueClass="text-success"
        />
        <Tile
          label="Průměr — pozdě"
          value={data.pozde > 0 ? `${data.avg_days_late.toFixed(1)} dní` : '—'}
          note="u zakázek po termínu"
          valueClass={data.pozde > 0 ? 'text-danger' : 'text-text'}
        />
        <Tile
          label="Stornováno"
          value={`${pctStorno.toFixed(0)} %`}
          note={`${data.stornovano} z ${celkem} · čeká na odeslání ${data.ceka}`}
        />
        <Tile
          label="Objednávky v prodlení"
          value={String(data.v_prodleni)}
          note={data.v_prodleni > 0 ? 'klikni pro seznam ↓' : 'žádná nemešká'}
          valueClass={data.v_prodleni > 0 ? 'text-danger' : 'text-success'}
          onClick={toggleProdleni}
        />
      </div>

      {prodleniOpen && (
        <div className="mt-3 bg-surface rounded-xl shadow-card overflow-hidden">
          {prodleniErr ? (
            <p className="px-5 py-4 text-sm text-danger">{prodleniErr}</p>
          ) : prodleniOrders === null ? (
            <p className="px-5 py-4 text-sm text-text-muted">Načítám…</p>
          ) : prodleniOrders.length === 0 ? (
            <p className="px-5 py-4 text-sm text-text-muted">Žádná objednávka nemešká. 🎉</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-text-secondary bg-bg">
                    <th className="px-4 py-2">Kód</th>
                    <th className="px-4 py-2">Slíbený termín</th>
                    <th className="px-4 py-2">Meškáme</th>
                    <th className="px-4 py-2">E-mail</th>
                    <th className="px-4 py-2 text-right">Hodnota</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {prodleniOrders.map((o) => (
                    <tr key={o.order_code} className="border-t border-border-light">
                      <td className="px-4 py-2 font-mono font-semibold text-text">
                        {o.order_code}
                      </td>
                      <td className="px-4 py-2 text-text-secondary">{fmtDay(o.deadline)}</td>
                      <td className="px-4 py-2 font-semibold text-danger whitespace-nowrap">
                        {fmtDny(o.days_late)}
                      </td>
                      <td className="px-4 py-2 text-text-secondary">{o.customer_email ?? '—'}</td>
                      <td className="px-4 py-2 text-right text-text whitespace-nowrap">
                        {o.total_with_vat != null
                          ? `${o.total_with_vat.toLocaleString('cs-CZ')} ${o.currency_code ?? ''}`
                          : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <a
                          href={o.shoptet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary font-semibold hover:underline whitespace-nowrap"
                        >
                          Otevřít v Shoptetu ↗
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Skupinový sloupcový graf — 12 měsíců, v každém jeden sloupec na rok. */
function Chart({
  rows,
  metric,
  hover,
  onHover,
}: {
  rows: YearRow[];
  metric: Metric;
  hover: number | null;
  onHover: (m: number | null) => void;
}) {
  const W = 1000;
  const H = 360;
  const padL = 72;
  const padB = 30;
  const padT = 16;
  const max = Math.max(1, ...rows.flatMap((y) => y.months.map((m) => metricValue(m, metric))));
  const step = niceStep(max / 5);
  const top = Math.ceil(max / step) * step;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const gw = (W - padL - 10) / 12;
  const bw = (gw * 0.8) / rows.length;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full min-w-[680px] h-auto"
        role="img"
        aria-label="Hodnota vyrobeného zboží po měsících a rocích"
      >
        <title>Hodnota vyrobeného zboží po měsících a rocích</title>
        {ticks.map((t) => {
          const y = H - padB - ((H - padB - padT) * t) / top;
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={W - 10} y2={y} stroke="#e5e7eb" strokeWidth="1" />
              <text x={padL - 8} y={y + 4} fontSize="12" textAnchor="end" fill="#6b7280">
                {metric === 'value' ? fmtAxis(t) : Math.round(t).toLocaleString('cs-CZ')}
              </text>
            </g>
          );
        })}
        {MONTHS_SHORT.map((name, i) => {
          const gx = padL + i * gw;
          const active = hover === i;
          return (
            <g key={name} onMouseEnter={() => onHover(i)} onMouseLeave={() => onHover(null)}>
              <rect
                x={gx}
                y={padT}
                width={gw}
                height={H - padB - padT}
                fill={active ? '#f6fcff' : 'transparent'}
              />
              {rows.map((y, yi) => {
                const v = metricValue(y.months[i], metric);
                const h = ((H - padB - padT) * v) / top;
                const x = gx + gw * 0.1 + yi * bw;
                return v > 0 ? (
                  <rect
                    key={y.year}
                    x={x}
                    y={H - padB - h}
                    width={bw * 0.9}
                    height={h}
                    rx="2"
                    fill={color(yi, rows.length)}
                  />
                ) : null;
              })}
              <text
                x={gx + gw / 2}
                y={H - padB + 16}
                fontSize="12"
                textAnchor="middle"
                fill={active ? '#374151' : '#6b7280'}
                fontWeight={active ? 'bold' : 'normal'}
              >
                {name}
              </text>
            </g>
          );
        })}
        <line x1={padL} y1={H - padB} x2={W - 10} y2={H - padB} stroke="#9ca3af" strokeWidth="1" />
        {hover !== null && (
          <Tooltip rows={rows} metric={metric} month={hover} gw={gw} padL={padL} width={W} />
        )}
      </svg>
    </div>
  );
}

/**
 * Bublina nad sloupcem s částkami za jednotlivé roky. Kreslí se v SVG (ne
 * HTML), ať se drží grafu i při vodorovném posunu na užší obrazovce.
 */
function Tooltip({
  rows,
  metric,
  month,
  gw,
  padL,
  width,
}: {
  rows: YearRow[];
  metric: Metric;
  month: number;
  gw: number;
  padL: number;
  width: number;
}) {
  const lineH = 18;
  const boxW = 168;
  const boxH = 14 + rows.length * lineH;
  const center = padL + month * gw + gw / 2;
  // U krajních měsíců bublinu přisadíme, ať nevyleze mimo plochu grafu.
  const x = Math.min(Math.max(center - boxW / 2, 4), width - boxW - 4);

  return (
    <g pointerEvents="none">
      <rect x={x} y={6} width={boxW} height={boxH} rx="6" fill="#ffffff" stroke="#cbd5e1" />
      {[...rows].reverse().map((y, i) => {
        const v = metricValue(y.months[month], metric);
        const idx = rows.length - 1 - i;
        return (
          <g key={y.year}>
            <rect
              x={x + 10}
              y={6 + 12 + i * lineH}
              width={9}
              height={9}
              rx="2"
              fill={color(idx, rows.length)}
            />
            <text x={x + 25} y={6 + 20 + i * lineH} fontSize="12" fill="#6b7280">
              {y.year}
            </text>
            <text
              x={x + boxW - 10}
              y={6 + 20 + i * lineH}
              fontSize="12"
              fontWeight="bold"
              textAnchor="end"
              fill="#374151"
            >
              {v > 0 ? fmtMetric(v, metric) : '—'}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/** Krok mřížky zaokrouhlený na 1/2/5 × mocninu deseti. */
function niceStep(raw: number): number {
  const pow = 10 ** Math.floor(Math.log10(Math.max(1, raw)));
  const n = raw / pow;
  const mult = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return mult * pow;
}

/** 'YYYY-MM-DD' → '3.8.'. */
function fmtDay(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${Number(d)}.${Number(m)}.` : iso;
}

/** 'YYYY-MM' → 'srpna 2026'. */
function fmtMonth(key: string): string {
  const [y, m] = key.split('-');
  const names = [
    'ledna',
    'února',
    'března',
    'dubna',
    'května',
    'června',
    'července',
    'srpna',
    'září',
    'října',
    'listopadu',
    'prosince',
  ];
  return y && m ? `${names[Number(m) - 1] ?? m} ${y}` : key;
}

/**
 * Plošný graf s body — používá se pro denní naskladnění i pro měsíční výkon
 * švadlen. Bez knihovny, ať PDF i stránka zůstanou lehké.
 */
function AreaChart({
  points,
  everyNth = 1,
  scale = 1,
  fmtValue = fmtCzk,
  emptyLabel = 'nic naskladněno',
}: {
  points: { label: string; value: number }[];
  /** Kolikátý popisek na ose X vypsat — u hustých řad by se slily. */
  everyNth?: number;
  /** Zvětšení písma a bubliny — graf v půlce šířky se vykresluje menší. */
  scale?: number;
  /** Formátování hodnoty v bublině — default koruny, jiné grafy si dají vlastní. */
  fmtValue?: (n: number) => string;
  /** Text v bublině, když je hodnota 0. */
  emptyLabel?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 900;
  const H = 300;
  const padL = 70;
  const padB = 52;
  const padT = 14;
  const max = Math.max(1, ...points.map((p) => p.value));
  const step = niceStep(max / 4);
  const top = Math.ceil(max / step) * step;
  const ticks = Array.from({ length: Math.round(top / step) + 1 }, (_, i) => i * step);
  const dx = points.length > 1 ? (W - padL - 20) / (points.length - 1) : 0;
  const x = (i: number) => padL + i * dx;
  const y = (v: number) => H - padB - ((H - padB - padT) * v) / top;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.value)}`).join(' ');
  const area = `${line} L ${x(points.length - 1)} ${H - padB} L ${x(0)} ${H - padB} Z`;

  // Bublina s hodnotou — clamp, ať u krajů nevyleze z plochy grafu.
  const fs = (n: number) => Math.round(n * scale);
  const boxW = Math.round(190 * scale);
  const boxH = Math.round(46 * scale);
  const hovered = hover !== null ? points[hover] : undefined;
  const boxX = hover === null ? 0 : Math.min(Math.max(x(hover) - boxW / 2, 4), W - boxW - 4);
  const boxY = hovered ? Math.max(6, y(hovered.value) - boxH - 12) : 6;

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full min-w-[600px] h-auto"
        role="img"
        aria-label="Vývoj hodnoty v čase"
      >
        <title>Vývoj hodnoty v čase</title>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} y1={y(t)} x2={W - 10} y2={y(t)} stroke="#e5e7eb" strokeWidth="1" />
            <text x={padL - 8} y={y(t) + 4} fontSize={fs(11)} textAnchor="end" fill="#6b7280">
              {fmtAxis(t)}
            </text>
          </g>
        ))}
        {points.length > 1 && (
          <>
            <path d={area} fill="#8abe1a" fillOpacity="0.18" />
            <path d={line} fill="none" stroke="#8abe1a" strokeWidth="2" />
          </>
        )}
        {hover !== null && (
          <line
            x1={x(hover)}
            y1={padT}
            x2={x(hover)}
            y2={H - padB}
            stroke="#8abe1a"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        )}
        {points.map((p, i) => (
          <g key={p.label}>
            {/* Neviditelný pruh přes celou výšku — trefit se do něj je snadné. */}
            <rect
              x={x(i) - Math.max(dx / 2, 6)}
              y={padT}
              width={Math.max(dx, 12)}
              height={H - padB - padT}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
            {p.value > 0 && (
              <circle
                cx={x(i)}
                cy={y(p.value)}
                r={hover === i ? fs(5) : fs(3)}
                fill={hover === i ? '#719e10' : '#8abe1a'}
                pointerEvents="none"
              />
            )}
            {i % everyNth === 0 && (
              <text
                x={x(i)}
                y={H - padB + 14}
                fontSize={fs(10)}
                textAnchor="end"
                fill={hover === i ? '#374151' : '#6b7280'}
                fontWeight={hover === i ? 'bold' : 'normal'}
                transform={`rotate(-45 ${x(i)} ${H - padB + 14})`}
                pointerEvents="none"
              >
                {p.label}
              </text>
            )}
          </g>
        ))}
        <line x1={padL} y1={H - padB} x2={W - 10} y2={H - padB} stroke="#9ca3af" strokeWidth="1" />
        {hovered && (
          <g pointerEvents="none">
            <rect
              x={boxX}
              y={boxY}
              width={boxW}
              height={boxH}
              rx="6"
              fill="#ffffff"
              stroke="#cbd5e1"
            />
            <text x={boxX + fs(12)} y={boxY + fs(19)} fontSize={fs(12)} fill="#6b7280">
              {hovered.label}
            </text>
            <text
              x={boxX + fs(12)}
              y={boxY + fs(36)}
              fontSize={fs(13)}
              fontWeight="bold"
              fill={hovered.value > 0 ? '#374151' : '#9ca3af'}
            >
              {hovered.value > 0 ? fmtValue(hovered.value) : emptyLabel}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}
